/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import { StyledModal, ModalActions, ButtonGroup, Button, useToast } from 'sarsaparilla';
import * as actions from '../../actions/tripPreferences';
import TripPreferencesForm from './TripPreferencesForm';
import { useAppDispatch } from '../../redux/redux-helpers';

interface PreferencesModalProps {
    checked: string[];
    isOpen: boolean;
    isEnabled: boolean;
    closeModal: (v?: boolean) => void;
    onChange: (v: string[]) => void;
    preferences: Array<string>;
    onApplyPreferences: (v?: Array<string>) => void;
}

function PreferencesManagementModal({
    isOpen,
    closeModal,
    onChange,
    checked,
    preferences,
    isEnabled,
    onApplyPreferences,
}: PreferencesModalProps) {
    const showToast = useToast();
    const [loading, setLoading] = React.useState<boolean>(false);
    const dispatch = useAppDispatch();

    const updateTripPreferences = async (): Promise<void> => {
        setLoading(true);
        try {
            await dispatch(actions.updateTripPreferences(checked));
            if (isEnabled) {
                onApplyPreferences();
            }
            setLoading(false);
            closeModal();
            showToast('Success! Your trip preferences have been updated!', {
                type: 'success',
            });
        } catch {
            showToast(
                'There was a problem updating your trip preferences, please try again.',
                {
                    type: 'error',
                }
            );
            setLoading(false);
            closeModal();
        }
    };

    const gaPreferences = checked;

    return (
        <StyledModal
            shouldShowCloseButton
            shouldActionsStickInMobile
            size="md"
            isOpen={isOpen}
            heading="Trip Preferences"
            onRequestClose={closeModal}
        >
            <p className="pb-2">
                Set your preferences below to keep your searches focused on campsites of
                interest to you. We will also use your preferences to improve the
                recommendations we provide.{' '}
            </p>

            <TripPreferencesForm onChange={onChange} checked={checked} />

            <ModalActions>
                <ButtonGroup isFullWidthOnMobile={false} isStretchedToFit>
                    <Button
                        appearance="tertiary"
                        // @ts-ignore
                        onClick={closeModal}
                        isDisabled={loading}
                        clickTagCategory="Search"
                        clickTagAction={
                            preferences?.length > 0
                                ? `Cancel / Active Filters - ${preferences}`
                                : `No Preferences Selected`
                        }
                        clickTagLabel="Trip Preferences Modal"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={updateTripPreferences}
                        isLoading={loading}
                        clickTagCategory="Search"
                        clickTagAction={
                            gaPreferences?.length > 0
                                ? `Selected / Active Filters - ${gaPreferences}`
                                : `No Preferences Selected`
                        }
                        clickTagLabel="Trip Preferences Modal"
                    >
                        Update Trip Preferences
                    </Button>
                </ButtonGroup>
            </ModalActions>
        </StyledModal>
    );
}

export default PreferencesManagementModal;
